import { useEffect, useRef, useState } from 'react'
import { useInfiniteQuery } from 'react-query'
import { CiSearch } from 'react-icons/ci'
import InfiniteScroll from 'react-infinite-scroll-component'
import DotsLoading from 'components/loadings/dotsLoading'
import { getMonyPoolsDetailUsers } from 'services/fund'
import { IRequestTarget } from '../useRequestDraft'

/**
 * «این درخواست برای کیست؟» — the manager-only first step.
 *
 * Android has always let a manager file a request for a member who asked in
 * person; the PWA had no way to express it, so those members either went
 * unrecorded or the manager filed it under their own name. This is that gap.
 *
 * Ordinary members never see this step: `poolship_id` defaults to the caller
 * server-side and the backend refuses a non-manager naming somebody else, so
 * showing them a picker would be offering a choice that cannot be made.
 */
export default function StepWho({
    moneypoolId,
    value,
    self,
    onPick,
}: {
    moneypoolId?: string
    value?: IRequestTarget
    /** the manager's own membership, offered first */
    self?: IRequestTarget
    onPick: (target: IRequestTarget) => void
}) {
    const [searchInput, setSearchInput] = useState('')
    const [searchQuery, setSearchQuery] = useState('')
    const usedSearch = useRef(false)

    // `/poolships/` is cursor-paginated; fetching one page would make most
    // members of any fund past ~20 people unpickable unless you already knew
    // the name to search for.
    const { data, isLoading, fetchNextPage, hasNextPage } = useInfiniteQuery(
        ['loan-request-members', moneypoolId, searchQuery.trim()],
        ({ pageParam = 1 }) =>
            getMonyPoolsDetailUsers(pageParam, moneypoolId, searchQuery.trim()),
        {
            getNextPageParam: (_lastPage, allPages) => {
                const last = allPages[allPages.length - 1]?.next || ''
                return last
                    ? new URL(last).searchParams.get('cursor')
                    : undefined
            },
            enabled: Boolean(moneypoolId),
        }
    )

    useEffect(() => {
        if (searchInput === '') setSearchQuery('')
    }, [searchInput])

    const rows = (data?.pages ?? []).flatMap((page) => page?.results ?? [])

    return (
        <div>
            <div className=" flex items-center gap-2 rounded-full border border-stroke bg-white px-3 py-2">
                <CiSearch size={18} className=" text-text-secondary" />
                <input
                    value={searchInput}
                    onChange={(event) => setSearchInput(event.target.value)}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                            if (searchInput.trim()) usedSearch.current = true
                            setSearchQuery(searchInput)
                        }
                    }}
                    placeholder="جست‌وجوی نام عضو"
                    className=" w-full bg-transparent text-xs outline-none"
                />
            </div>

            {self && (
                <button
                    type="button"
                    onClick={() => onPick(self)}
                    className={` mt-3 flex w-full items-center justify-between rounded-2xl border bg-white px-4 py-3 text-right ${
                        value?.poolship_id === self.poolship_id
                            ? ' border-green-1'
                            : ' border-stroke'
                    }`}
                >
                    <span className=" text-xs font-bold text-text-body">
                        برای خودم
                    </span>
                    <span className=" text-[11px] text-text-secondary">
                        {self.name}
                    </span>
                </button>
            )}

            {isLoading && <DotsLoading />}

            <div id="loan-request-members-scroll" className=" mt-3">
                <InfiniteScroll
                    dataLength={rows.length}
                    next={fetchNextPage}
                    hasMore={Boolean(hasNextPage)}
                    loader={<DotsLoading />}
                    scrollableTarget="loan-request-members-scroll"
                >
                    <ul className=" rounded-2xl border border-stroke bg-white px-4">
                        {rows
                            .filter(
                                (row: any) =>
                                    row?.id !== self?.poolship_id
                            )
                            .map((row: any) => {
                                const name = `${row?.member?.first_name ?? ''} ${
                                    row?.member?.last_name ?? ''
                                }`.trim()
                                const picked =
                                    value?.poolship_id === row?.id
                                return (
                                    <li
                                        key={row?.id}
                                        role="button"
                                        tabIndex={0}
                                        onClick={() =>
                                            onPick({
                                                poolship_id: row.id,
                                                name,
                                            })
                                        }
                                        onKeyDown={(event) => {
                                            if (
                                                event.key === 'Enter' ||
                                                event.key === ' '
                                            ) {
                                                event.preventDefault()
                                                onPick({
                                                    poolship_id: row.id,
                                                    name,
                                                })
                                            }
                                        }}
                                        className=" flex cursor-pointer items-center justify-between gap-2 border-b border-divider py-3 last:border-b-0"
                                    >
                                        <span className=" min-w-0 truncate text-xs font-bold text-text-body">
                                            {name || '—'}
                                        </span>
                                        {picked && (
                                            <span className=" shrink-0 text-[11px] font-bold text-green-1">
                                                انتخاب شد
                                            </span>
                                        )}
                                    </li>
                                )
                            })}
                    </ul>
                </InfiniteScroll>
            </div>

            {!isLoading && rows.length === 0 && (
                <p className=" mt-4 text-center text-[11px] text-text-muted">
                    عضوی پیدا نشد.
                </p>
            )}
        </div>
    )
}
